import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ComponentProps, FC } from "react";
import { SystemLinearMeter } from "../../../components/UIToolkit/DataViz/SystemLinearMeter/SystemLinearMeter";
import type { MilestoneProgressBar } from "../../../components/UIToolkit/Graphs/MilestoneProgressBar/MilestoneProgressBar";
import { InfoIconWithTooltip } from "../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { ICON_SIZE } from "../../../theme/tokens";

// The same tone set as the action cards' progress bars on this tab — read off that prop rather than
// restated, so the two kinds of bar cannot drift to different vocabularies.
export type PlanCostMeterTone = NonNullable<ComponentProps<typeof MilestoneProgressBar>["tone"]>;

type Props = {
	/** Already-translated name of the running figure ("Spent so far"). */
	label: string;
	/** Already-formatted running figure. */
	value: string;
	/** The running figure and the scale it fills against, both in dollars. */
	amount: number;
	max: number;
	/**
	 * Share of the plan elapsed, 0–1 — drawn as the "expected by now" tick the caption names. Omit when
	 * there is no expectation to mark (spend on a plan that budgeted none). A share of 0 draws no tick
	 * either: a plan with nothing elapsed expects nothing yet, and a tick on the track's left cap reads
	 * as a stray mark rather than as a target.
	 */
	expectedShare?: number;
	tone: PlanCostMeterTone;
	/** Already-translated "expected to date" caption under the bar. */
	caption: string;
	/**
	 * Already-translated explanation of what the figure counts, behind an ⓘ on the label. Two meters
	 * side by side would otherwise carry two paragraphs each, and the panel would tower over the
	 * status card it sits beside.
	 */
	basis: string;
	ariaLabel: string;
	dataTestId: string;
};

// The tick and the inert track are tints of ink rather than the tone, so an empty meter reads empty
// and the expected-by-now line stays legible over any fill.
const TRACK_ALPHA = 0.08;
const EXPECTED_TICK_ALPHA = 0.55;

/**
 * One side of the plan's money — spend or savings — as a figure over a meter with an "expected by
 * now" tick. Used only once a plan has cuts, when the cost panel carries two such sides that have to
 * read as a matched pair; an add-only plan keeps its original single bar.
 */
export const PlanCostMeter: FC<Props> = ({ label, value, amount, max, expectedShare, tone, caption, basis, ariaLabel, dataTestId }) => {
	const theme = useTheme();
	return (
		<Box data-testid={dataTestId}>
			<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "baseline", gap: 1, mb: 0.75 }}>
				<Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
					<Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
						{label}
					</Typography>
					<InfoIconWithTooltip title={basis} iconSx={{ fontSize: ICON_SIZE.PILL, color: "text.disabled" }} dataTestId={`${dataTestId}-basis`} />
				</Stack>
				<Typography
					variant="subtitle2"
					sx={{
						fontWeight: 700,
						fontVariantNumeric: "tabular-nums",
						color: tone === "error" || tone === "warning" ? `${tone}.dark` : "text.primary",
					}}
					data-testid={`${dataTestId}-value`}
				>
					{value}
				</Typography>
			</Stack>
			<SystemLinearMeter
				value={amount}
				max={max}
				color={theme.palette[tone].main}
				trackColor={alpha(theme.palette.text.primary, TRACK_ALPHA)}
				targetRatio={expectedShare !== undefined && expectedShare > 0 ? expectedShare : undefined}
				targetColor={alpha(theme.palette.text.primary, EXPECTED_TICK_ALPHA)}
				ariaLabel={ariaLabel}
				valueText={value}
				dataTestId={`${dataTestId}-bar`}
			/>
			<Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary" }}>
				{caption}
			</Typography>
		</Box>
	);
};
